import React from 'react';
import { FormControl, InputLabel, Select, MenuItem, CircularProgress, SelectChangeEvent } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { updateTicketIdentification } from '../../../../state';
import { useGetTicketCategoriesQuery } from '../../../../../../services/DimensionService/dimensionApi';
import { EnumView } from '../../../../../../services/DimensionService/types/dimensionTypes';

interface CategoryEditProps {
  disabled?: boolean;
}

// Filter out PM as a selectable option — Preventive Maintenance tickets are created only via
// recurring tasks — but keep it if the ticket already has it, so existing PM tickets still render.
const filterSelectableCategories = (categories: EnumView[] | undefined, currentCategory: string | undefined): EnumView[] =>
  categories?.filter(category => category.value !== 'PM' || currentCategory === 'PM') || [];

const renderCategoryOptions = (isLoading: boolean, error: unknown, categories: EnumView[]) => {
  if (isLoading) {
    return (
      <MenuItem disabled>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        Loading categories...
      </MenuItem>
    );
  }
  if (error) {
    return <MenuItem disabled>Error loading categories</MenuItem>;
  }
  return categories.map((category) => (
    <MenuItem key={category.value} value={category.value}>
      {category.name}
    </MenuItem>
  ));
};

const CategoryEdit: React.FC<CategoryEditProps> = ({ disabled = false }) => {
  const dispatch = useAppDispatch();
  const { currentTicket } = useAppSelector(state => state.ticket);
  const { data: categories, isLoading, error } = useGetTicketCategoriesQuery();

  const filteredCategories = filterSelectableCategories(categories, currentTicket?.identification.category);

  const handleChange = (event: SelectChangeEvent) => {
    if (!disabled) {
      dispatch(updateTicketIdentification({ category: event.target.value }));
    }
  };

  return (
    <FormControl size="small" sx={{ minWidth: 200 }} required>
      <InputLabel>Category *</InputLabel>
      <Select
        value={currentTicket?.identification.category || ''}
        onChange={handleChange}
        label="Category *"
        disabled={disabled || isLoading || !!error}
      >
        {renderCategoryOptions(isLoading, error, filteredCategories)}
      </Select>
    </FormControl>
  );
};

export default CategoryEdit;
